import { Card, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { FilePicker } from './file-picker';
import { FileInfo, FileInfoInput } from '../../../../../../generated';
import FileSelectionCard from '../../../../../shared/file-selection';

interface VerificationPacketDocumentPickerProps {
  onMergePdfs: (blobPaths: FileInfoInput[]) => Promise<void>;
  blobUrls: FileInfo[];
  verificationFileNames: Map<string, string>;
  isProcessingMergingPdfs: boolean;
  onCancel: () => void;
}

export interface SelectedFileInfo {
  name: string;
  url: string;
  type: string;
  caseType: string;
  caseId: string;
}

export interface SelectedFilesMap {
  [key: string]: SelectedFileInfo | null;
}

export const VerificationPacketDocumentPicker: React.FC<VerificationPacketDocumentPickerProps> = (props) => {
  const initialFiles = {
    'verification-form': {
      name: props.blobUrls[0]?.name ?? '',
      url: props.blobUrls[0]?.url || '',
      type: props.blobUrls[0]?.type || "application/pdf",
      caseType: '',
      caseId: ''
    },
    credential: {
      name: props.blobUrls[1]?.name ?? '',
      url: props.blobUrls[1]?.url || '',
      type: props.blobUrls[1]?.type || '',
      caseType: '',
      caseId: ''
    },
    'redacted-notarized-id-form': {
      name: props.blobUrls[2]?.name ?? '',
      url: props.blobUrls[2]?.url || '',
      type: props.blobUrls[2]?.type || "application/pdf",
      caseType: '',
      caseId: ''
    }
  };

  function useFileSelection(initialFiles: SelectedFilesMap) {
    const [selectedFilesMap, setSelectedFilesMap] = useState<SelectedFilesMap>(initialFiles);
    const [currentFileSelection, setCurrentFileSelection] = useState<string>('');

    const handleFileSelection = (fileInfo: SelectedFileInfo) => {
      setSelectedFilesMap((prevFilesMap) => ({
        ...prevFilesMap,
        [currentFileSelectionRef.current]: fileInfo
      }));
    };

    const handleCancel = () => {
      props.onCancel();
      setCurrentFileSelection('');
      setSelectedFilesMap(initialFiles);
    };

    return {
      selectedFilesMap,
      currentFileSelection,
      setCurrentFileSelection,
      handleFileSelection,
      onCancel: handleCancel
    };
  }

  const { selectedFilesMap, currentFileSelection, setCurrentFileSelection, handleFileSelection, onCancel: handleCancel } = useFileSelection(initialFiles);

  const handleGenerateDocument = async () => {
    const requiredFiles = ['verification-form', 'credential', 'redacted-notarized-id-form'];
    const allFilesPresent = requiredFiles.every((file) => selectedFilesMap[file]?.name?.trim());
    if (!allFilesPresent) {
      message.error('Please select all the files');
      return;
    }
    console.log('selectedFilesMap', selectedFilesMap); 
    const blobPaths: FileInfoInput[] = [];
    for (const key in selectedFilesMap) {
      const fileInfo = selectedFilesMap[key];
      if (fileInfo) {
        const blobInfo: FileInfoInput = {
          fileName: fileInfo.name,
          fileType: fileInfo.type
        }
        blobPaths.push(blobInfo);
      }
    }
    await props.onMergePdfs(blobPaths);
  };

  const files = [
    {
      type: 'verification-form',
      label: 'Select',
      fileName: selectedFilesMap['verification-form']?.name || 'verification-form.pdf',
      isRequired: true
    },
    {
      type: 'credential',
      label: 'Select',
      fileName: selectedFilesMap.credential?.name || 'credential',
      isRequired: true
    },
    {
      type: 'redacted-notarized-id-form',
      label: 'Select',
      fileName: selectedFilesMap['redacted-notarized-id-form']?.name || 'redacted-notarized-id-form.pdf',
      isRequired: true
    }
  ];

  const currentFileSelectionRef = useRef(currentFileSelection);
  useEffect(() => {
    currentFileSelectionRef.current = currentFileSelection;
  }, [currentFileSelection]);

  const currentFilePicker = `File Picker - ${currentFileSelection}`;
  return (
    <div
      style={{
        display: 'flex'
      }}
    >
      <FileSelectionCard
        title="Generate Verification Packet"
        files={files}
        selectedFilesMap={selectedFilesMap}
        onFileSelect={setCurrentFileSelection}
        currentFileSelection={currentFileSelection}
        onCancel={handleCancel}
        onGenerateDocument={handleGenerateDocument}
        isProcessingMergingPdfs={props.isProcessingMergingPdfs}
      />
      {currentFileSelection !== '' && (
        <Card title={currentFilePicker}>
          <FilePicker handleFileSelection={handleFileSelection} />
        </Card>
      )}
    </div>
  );
};
